// Card Statements — the second sub-view on the Reconciliation page
// (2026-07-12, classification + multi-account v1.1 same day).
// Upload a monthly statement CSV — the firm Amex, the office card, or
// a bank account (spend-negative files are detected and flipped).
// Every charge lands in a pile:
//   ✓ Matched       — charge ↔ receipt (receipt carries the PO/project)
//   ✓ Classified    — Cimon said what it is: a PO, office, reimbursable,
//                     personal, or card fees
//   ⚠ Needs sorting — nothing known yet
//   ⚠ No card charge — receipt in the window no charge matched (derived
//                     live, never stored)
// "Copy list for Dan" renders the classified statement as plain
// bookkeeper text (vendor / amount / PO number / project — no Gilda
// jargon; Dan lives in QB and reads Gilda poorly).
//
// NOTHING here writes to receipts / POs / items / vendor_payments —
// statement lines are a cross-reference layer, never an editor.
// Parsing + matching are deterministic (src/lib/cardStatements.js) —
// no AI, no QB writes. Data loads on demand here (not in AppContext
// boot — only this page needs it).
//
// Guard tests: cardStatements.test.js (logic),
// cardStatementsSection.test.jsx (wiring).

import React, { useState, useEffect, useMemo } from 'react';
import { useAppContext } from './AppContext';
import { supabase } from '../lib/supabase';
import { fmtMoney, hasAdminAccess, poSubtotalLive, calcVendorDeposit } from '../utils';
import { jumpTo } from '../lib/jump';
import {
  parseCardStatementCsv, matchCardStatementLines, receiptsWithoutCharge,
  splitStatementByMonth,
  detectSpendNegative, flipStatementSigns, suggestPosForDescriptor,
  CARD_LINE_CATEGORIES, cardLineCategoryLabel, buildStatementSummaryText,
  buildDanSpreadsheetRows, spreadsheetRowsToCsv,
  matchIncomingDeposits, suggestDepositSource, describeRecordedPayment,
  describeRecordedPaymentDocs, statementReconciliationState,
  rowToCardStatement, rowToCardStatementLine, cardStatementLineToRow,
} from '../lib/cardStatements';

const COLOR_TEXT      = '#111111';
const COLOR_MUTED     = '#888880';
const COLOR_BORDER    = '#E0E0DC';
const COLOR_BORDER_DK = '#D8D7D2';
const COLOR_ALERT     = '#D4007A';
const COLOR_PAID      = '#1A5C38';
const COLOR_GROUND    = '#FFFFFF';

const cell = {
  padding: '8px 8px', fontSize: 11, color: COLOR_TEXT,
  borderBottom: '0.5px solid ' + COLOR_BORDER, verticalAlign: 'middle',
};
const cellRight = Object.assign({}, cell, { textAlign: 'right', whiteSpace: 'nowrap' });
const head = {
  padding: '8px 8px', fontSize: 9, fontWeight: 500, letterSpacing: '0.07em',
  textTransform: 'uppercase', color: COLOR_MUTED, textAlign: 'left',
  borderBottom: '0.5px solid ' + COLOR_BORDER,
};
const headRight = Object.assign({}, head, { textAlign: 'right' });
const selectStyle = {
  height: 24, fontSize: 10, fontFamily: 'inherit',
  border: '0.5px solid ' + COLOR_BORDER_DK, borderRadius: 4,
  background: COLOR_GROUND, color: COLOR_TEXT,
};

function sectionTitle(text, count, color) {
  return (
    <div style={{ fontSize: 12, fontWeight: 500, color: color || COLOR_TEXT, margin: '22px 0 8px' }}>
      {text} <span style={{ color: COLOR_MUTED, fontWeight: 400 }}>· {count}</span>
    </div>
  );
}

function ghostBtn(extra) {
  return Object.assign({
    height: 24, padding: '0 10px', borderRadius: 4,
    border: '0.5px solid ' + COLOR_BORDER_DK, background: COLOR_GROUND,
    color: COLOR_TEXT, fontSize: 10, fontWeight: 500, fontFamily: 'inherit',
    cursor: 'pointer', whiteSpace: 'nowrap',
  }, extra || {});
}

const STATEMENT_COLS = 'id, period, label, filename, uploaded_by, line_count, created_at';

// Guess the account name from the file name so the field usually
// pre-fills itself ("activity.csv" from Amex → 'Amex').
// Trigger a browser download of CSV text. Excel-ready (the builder
// prepends a UTF-8 BOM).
function downloadCsv(filename, csvText) {
  const blob = new Blob([csvText], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function guessAccountName(filename) {
  const f = String(filename || '').toLowerCase();
  if (f.indexOf('amex') >= 0 || f.indexOf('american') >= 0 || f.indexOf('activity') >= 0) return 'Amex';
  if (f.indexOf('chase') >= 0) return 'Chase';
  if (f.indexOf('checking') >= 0 || f.indexOf('bank') >= 0) return 'Bank account';
  return '';
}

export default function CardStatementsSection() {
  const ctx = useAppContext();
  const receipts       = ctx.receipts || [];
  const vendors        = ctx.vendors || [];
  const purchaseOrders = ctx.purchaseOrders || [];
  const clientPayments = ctx.clientPayments || [];
  const paymentAllocations = ctx.paymentAllocations || [];
  const clients        = ctx.clients || [];
  const projects       = ctx.projects || [];
  const items          = ctx.items || [];
  const vendorPayments = ctx.vendorPayments || [];
  const canManage = ctx.isOwner || hasAdminAccess(ctx.userEmail, ctx.firmSettings);

  const [statements, setStatements] = useState(null);   // null = loading
  const [accountFilter, setAccountFilter] = useState('all');
  const [loadError, setLoadError]   = useState('');
  const [openId, setOpenId]         = useState(null);
  const [lines, setLines]           = useState(null);
  const [pending, setPending]       = useState(null);   // parsed upload awaiting save
  const [exportPeriod, setExportPeriod] = useState('');
  const [saving, setSaving]         = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [copied, setCopied]         = useState(false);
  // AI sorting suggestions, keyed by line id. SUGGESTIONS ONLY — each
  // renders as a chip with a one-click Accept; nothing auto-applies.
  const [suggestions, setSuggestions] = useState({});
  const [suggesting, setSuggesting]   = useState(false);
  const [suggestError, setSuggestError] = useState('');

  useEffect(function() {
    let alive = true;
    supabase.from('card_statements').select(STATEMENT_COLS)
      .order('period', { ascending: false })
      .order('created_at', { ascending: false })
      .then(function(res) {
        if (!alive) return;
        if (res.error) {
          const msg = String(res.error.message || '');
          setLoadError(msg.indexOf('card_statements') >= 0 || msg.indexOf('does not exist') >= 0
            ? 'The card-statement tables aren’t set up yet. Run supabase/migrations/20260712_create_card_statements.sql in the Supabase Dashboard, then reload.'
            : 'Could not load statements: ' + msg);
          setStatements([]);
          return;
        }
        setStatements((res.data || []).map(rowToCardStatement));
      });
    return function() { alive = false; };
  }, []);

  useEffect(function() {
    if (!openId) { setLines(null); return; }
    let alive = true;
    setLines(null);
    supabase.from('card_statement_lines').select('*')
      .eq('statement_id', openId)
      .order('line_date', { ascending: true })
      .then(function(res) {
        if (!alive) return;
        setLines(res.error ? [] : (res.data || []).map(rowToCardStatementLine));
      });
    setConfirmDelete(false);
    setCopied(false);
    setSuggestions({});
    setSuggestError('');
    return function() { alive = false; };
  }, [openId]);

  // ── Upload flow ─────────────────────────────────────────────────────

  function onPickFile(e) {
    const f = e.target.files && e.target.files[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = function() {
      const raw = String(reader.result || '');
      const parsed = parseCardStatementCsv(raw);
      setPending({
        filename: f.name,
        rawCsv: raw,
        parsedOriginal: parsed,
        flip: detectSpendNegative(parsed),
        period: (parsed.dateMax || '').slice(0, 7),
        account: guessAccountName(f.name),
      });
    };
    reader.readAsText(f);
    e.target.value = '';
  }

  // Known accounts for the upload picker — union of labels already used
  // on statements, the firm credit-card registry (Finance → cards), and
  // the standing 'Bank account'. "New account…" falls back to free text.
  const accountOptions = useMemo(function() {
    const out = [];
    function add(v) {
      const t = String(v || '').trim();
      if (t && out.indexOf(t) < 0) out.push(t);
    }
    (statements || []).forEach(function(st) { add(st.label); });
    (ctx.firmCreditCards || []).forEach(function(c) {
      add((c.label || '') + (c.last4 ? ' ··' + c.last4 : ''));
    });
    add('Bank account');
    return out;
  }, [statements, ctx.firmCreditCards]);

  // Relabel an existing statement's account (the pre-picker uploads all
  // landed with an empty label — Cimon needs Amex card / Chase Visa /
  // Chase bank distinguished). Admin-only page; writes label only.
  async function updateStatementAccount(statementId, label) {
    const res = await supabase.from('card_statements')
      .update({ label: label }).eq('id', statementId);
    if (res.error) { alert('Could not rename the account: ' + res.error.message); return; }
    setStatements(function(prev) {
      return (prev || []).map(function(st) {
        return st && st.id === statementId ? Object.assign({}, st, { label: label }) : st;
      });
    });
  }

  const pendingParsed = useMemo(function() {
    if (!pending) return null;
    return pending.flip ? flipStatementSigns(pending.parsedOriginal) : pending.parsedOriginal;
  }, [pending]);

  async function saveStatement() {
    if (!pending || !pendingParsed || saving) return;
    // Duplicate guard (2026-07-17): the same account + month already
    // uploaded would double every charge in the month export and match
    // the same receipts twice. Warn with specifics; proceeding is
    // allowed (re-uploads after a delete are legitimate).
    {
      const account = (pending.account || '').trim().toLowerCase();
      const periods = splitStatementByMonth(pendingParsed).map(function(g) { return g.period; });
      const dupes = (statements || []).filter(function(st) {
        return st && (st.label || '').trim().toLowerCase() === account
          && periods.indexOf(st.period) >= 0;
      });
      if (dupes.length) {
        const listText = dupes.map(function(st) {
          return '  ' + st.period + ' — ' + (st.filename || 'statement') + ' (' + st.lineCount + ' lines)';
        }).join('\n');
        const go = window.confirm(
          'This account already has a statement for the same month:\n\n' + listText +
          '\n\nUploading again will DOUBLE those charges in the month export.' +
          '\nIf this is a re-upload, delete the old statement first (open it \u2192 Delete).' +
          '\n\nUpload anyway?');
        if (!go) return;
      }
    }
    setSaving(true);
    try {
      // Match once across the whole file (greedy state spans months),
      // then file each line under its own calendar month — one
      // card_statements row per month, so every account aligns on the
      // same monthly buckets regardless of its statement cycle.
      const matches = matchCardStatementLines(pendingParsed.lines, {
        receipts: receipts, vendors: vendors, vendorPayments: vendorPayments,
      });
      const matchByLine = new Map();
      pendingParsed.lines.forEach(function(l, i) { matchByLine.set(l, matches[i] || { status: 'unmatched' }); });
      const receiptsById = {};
      receipts.forEach(function(r) { if (r && r.id) receiptsById[r.id] = r; });

      const groups = splitStatementByMonth(pendingParsed);
      const savedStatements = [];
      for (const g of groups) {
        const ins = await supabase.from('card_statements').insert({
          period: g.period,
          label: pending.account || '',
          filename: pending.filename,
          uploaded_by: ctx.userEmail || '',
          line_count: g.lines.length,
          raw_csv: pending.rawCsv,
        }).select(STATEMENT_COLS).single();
        if (ins.error) throw new Error(ins.error.message);
        const lineRows = g.lines.map(function(l) {
          const m = matchByLine.get(l);
          const rcpt = m.status === 'matched' ? receiptsById[m.receiptId] : null;
          return cardStatementLineToRow({
            statementId: ins.data.id,
            lineDate: l.lineDate,
            descriptor: l.descriptor,
            amount: l.amount,
            matchedReceiptId: m.status === 'matched' ? m.receiptId : null,
            // A vendor-payment match resolves the line to its PO — the
            // match_status CHECK has no 'payment' value, so it persists
            // as a PO assignment with the method recording how.
            poId: m.status === 'payment' ? (m.poId || null)
                : (rcpt ? (rcpt.poId || null) : null),
            matchStatus: m.status === 'credit' ? 'credit'
                       : m.status === 'matched' ? 'matched' : 'unmatched',
            matchMethod: (m.status === 'matched' || m.status === 'payment') ? m.method : '',
          });
        });
        if (lineRows.length) {
          const li = await supabase.from('card_statement_lines').insert(lineRows);
          if (li.error) throw new Error(li.error.message);
        }
        savedStatements.push(rowToCardStatement(ins.data));
      }
      setStatements(function(prev) { return savedStatements.concat(prev || []); });
      setPending(null);
      if (savedStatements.length) setOpenId(savedStatements[0].id);
    } catch (err) {
      alert('Could not save the statement: ' + (err && err.message ? err.message : err));
    } finally {
      setSaving(false);
    }
  }

  // ── Line mutations ──────────────────────────────────────────────────
  // These write ONLY to card_statement_lines. Receipts, POs, items, and
  // vendor payments are never touched from this surface.

  const PATCH_TO_CAMEL = {
    matched_receipt_id: 'matchedReceiptId', po_id: 'poId',
    match_status: 'matchStatus', match_method: 'matchMethod',
    category: 'category', notes: 'notes',
  };

  async function patchLine(lineId, patch) {
    const res = await supabase.from('card_statement_lines').update(patch).eq('id', lineId);
    if (res.error) { alert('Save failed: ' + res.error.message); return; }
    setLines(function(prev) {
      return (prev || []).map(function(l) {
        if (l.id !== lineId) return l;
        const next = Object.assign({}, l);
        Object.keys(patch).forEach(function(k) {
          if (PATCH_TO_CAMEL[k]) next[PATCH_TO_CAMEL[k]] = patch[k];
        });
        return next;
      });
    });
  }

  function linkReceipt(line, receiptId) {
    const rcpt = receipts.find(function(r) { return r.id === receiptId; });
    patchLine(line.id, {
      matched_receipt_id: receiptId,
      po_id: (rcpt && rcpt.poId) || null,
      match_status: 'matched',
      match_method: 'manual',
    });
  }

  function unlinkReceipt(line) {
    patchLine(line.id, {
      matched_receipt_id: null, po_id: null,
      match_status: 'unmatched', match_method: '',
    });
  }

  async function deleteStatement() {
    const res = await supabase.from('card_statements').delete().eq('id', openId);
    if (res.error) { alert('Delete failed: ' + res.error.message); return; }
    setStatements(function(prev) {
      return (prev || []).filter(function(s) { return s.id !== openId; });
    });
    setOpenId(null);
  }

  // ── Derived ─────────────────────────────────────────────────────────

  const receiptsById = useMemo(function() {
    const m = {};
    receipts.forEach(function(r) { if (r && r.id) m[r.id] = r; });
    return m;
  }, [receipts]);

  const posById = useMemo(function() {
    const m = {};
    purchaseOrders.forEach(function(p) { if (p && p.id) m[p.id] = p; });
    return m;
  }, [purchaseOrders]);

  const piles = useMemo(function() {
    const ls = lines || [];
    return {
      matched:    ls.filter(function(l) { return l.matchStatus === 'matched'; }),
      classified: ls.filter(function(l) {
        return l.matchStatus === 'unmatched' && (l.poId || l.category);
      }),
      needsSort:  ls.filter(function(l) {
        return l.matchStatus === 'unmatched' && !l.poId && !l.category;
      }),
      dismissed:  ls.filter(function(l) { return l.matchStatus === 'dismissed'; }),
      credits:    ls.filter(function(l) { return l.matchStatus === 'credit'; }),
    };
  }, [lines]);

  // The reverse pile derives LIVE from current receipts (never stored).
  const noCharge = useMemo(function() {
    if (!lines) return [];
    return receiptsWithoutCharge(lines, receipts);
  }, [lines, receipts]);

  // Incoming deposits (client-money side) — matched LIVE against
  // wizard-recorded payments; recording a missing one self-heals.
  const incoming = useMemo(function() {
    const credits = piles.credits;
    if (!credits.length) return [];
    const matches = matchIncomingDeposits(credits, clientPayments);
    return credits.map(function(l, i) { return { line: l, match: matches[i] }; });
  }, [piles.credits, clientPayments]);

  const paymentsById = useMemo(function() {
    const m = {};
    clientPayments.forEach(function(p) { if (p && p.id) m[p.id] = p; });
    return m;
  }, [clientPayments]);

  const linkOptions = useMemo(function() {
    if (!lines) return [];
    const taken = {};
    lines.forEach(function(l) { if (l.matchedReceiptId) taken[l.matchedReceiptId] = true; });
    return receiptsWithoutCharge(lines, receipts, { padDays: 21 })
      .filter(function(r) { return !taken[r.id]; });
  }, [lines, receipts]);

  const posSorted = useMemo(function() {
    return purchaseOrders.slice().sort(function(a, b) {
      return String(a.po_ref || '').localeCompare(String(b.po_ref || ''));
    });
  }, [purchaseOrders]);

  function receiptLabel(r) {
    const po = r.poId ? posById[r.poId] : null;
    return (r.vendorName || '—') + ' · ' + fmtMoney(parseFloat(r.totalAmount) || 0)
      + ' · ' + (r.receiptDate || 'no date') + (po ? ' · ' + (po.po_ref || '') : '');
  }

  function poOptionLabel(p) {
    return (p.po_ref || '—') + ' · ' + (p.vendorName || p.vendor_name || '')
      + (p.project ? ' · ' + p.project : '');
  }

  // Per-statement spreadsheet (detail view). Dan works in Excel — the
  // CSV opens there directly; the Account column doubles as his
  // filterable "tab" when months combine multiple accounts.
  function downloadStatementForDan() {
    const stmt = statements.find(function(s) { return s.id === openId; });
    if (!stmt) return;
    const rows = buildDanSpreadsheetRows(
      [{ statement: stmt, lines: lines || [] }],
      { receipts: receipts, purchaseOrders: purchaseOrders,
        clientPayments: clientPayments, paymentAllocations: paymentAllocations,
        invoices: ctx.invoices || [] });
    const name = ((stmt.period || 'statement') + '_' + (stmt.label || 'card') + '_charges_for_dan.csv')
      .replace(/\s+/g, '-');
    downloadCsv(name, spreadsheetRowsToCsv(rows));
  }

  // Whole-month spreadsheet (list view): every account's statement for
  // the chosen period in one file, Account column first.
  const [exporting, setExporting] = useState(false);

  // Reconciliation status per statement for the LIST view — a light
  // columns-only pull across all statements (a month is tens of rows,
  // a year is hundreds; fine in one query). Self-heals via openId: any
  // line edits happen in the detail view, so refetch when it closes.
  const [listLines, setListLines] = useState(null);
  useEffect(function() {
    if (openId !== null) return;   // list not visible
    let alive = true;
    supabase.from('card_statement_lines')
      .select('statement_id, match_status, po_id, category, amount, line_date, descriptor')
      .then(function(res) {
        if (!alive || res.error) return;
        const by = {};
        (res.data || []).forEach(function(row) {
          const l = rowToCardStatementLine(Object.assign({ id: 'x' }, row));
          (by[row.statement_id] = by[row.statement_id] || []).push(l);
        });
        setListLines(by);
      });
    return function() { alive = false; };
  }, [openId]);
  async function downloadMonthForDan(period) {
    if (exporting) return;
    const monthStatements = statements.filter(function(s) { return s.period === period; });
    if (!monthStatements.length) return;
    setExporting(true);
    try {
      const ids = monthStatements.map(function(s) { return s.id; });
      const res = await supabase.from('card_statement_lines').select('*').in('statement_id', ids);
      if (res.error) { alert('Could not load the month: ' + res.error.message); return; }
      const byStatement = {};
      (res.data || []).map(rowToCardStatementLine).forEach(function(l) {
        (byStatement[l.statementId] = byStatement[l.statementId] || []).push(l);
      });
      const rows = buildDanSpreadsheetRows(
        monthStatements.map(function(s) { return { statement: s, lines: byStatement[s.id] || [] }; }),
        { receipts: receipts, purchaseOrders: purchaseOrders,
          clientPayments: clientPayments, paymentAllocations: paymentAllocations,
          invoices: ctx.invoices || [] });
      downloadCsv(period + '_all-accounts_charges_for_dan.csv', spreadsheetRowsToCsv(rows));
    } finally {
      setExporting(false);
    }
  }

  // ── Per-charge notes ────────────────────────────────────────────────
  // Free text on any statement line ("split with personal", "vendor
  // said credit coming"). Saved to card_statement_lines.notes on blur;
  // flows into Dan's spreadsheet (Notes column) and the email list.
  const [noteEdit, setNoteEdit] = useState(null);   // { lineId, text }

  function chargeDescCell(l) {
    const editing = noteEdit && noteEdit.lineId === l.id;
    return (
      <div>
        <div>{l.descriptor}</div>
        {editing ? (
          <input
            autoFocus type="text" value={noteEdit.text}
            data-testid={'note-input-' + l.id}
            onChange={function(e) { setNoteEdit({ lineId: l.id, text: e.target.value }); }}
            onBlur={function() { patchLine(l.id, { notes: noteEdit.text.trim() }); setNoteEdit(null); }}
            onKeyDown={function(e) { if (e.key === 'Enter') e.target.blur(); }}
            placeholder="Note for this charge…"
            style={{ marginTop: 3, height: 22, width: '100%', maxWidth: 320, padding: '0 6px',
              border: '0.5px solid ' + COLOR_BORDER_DK, borderRadius: 4,
              fontSize: 10, fontFamily: 'inherit', boxSizing: 'border-box' }}
          />
        ) : l.notes ? (
          <div
            onClick={canManage ? function() { setNoteEdit({ lineId: l.id, text: l.notes }); } : undefined}
            title={canManage ? 'Click to edit note' : ''}
            style={{ fontSize: 10, color: COLOR_MUTED, fontStyle: 'italic', marginTop: 2,
              cursor: canManage ? 'pointer' : 'default' }}>
            ✎ {l.notes}
          </div>
        ) : canManage ? (
          <button
            onClick={function() { setNoteEdit({ lineId: l.id, text: '' }); }}
            data-testid={'add-note-' + l.id}
            style={{ marginTop: 2, padding: 0, border: 'none', background: 'none',
              color: COLOR_MUTED, fontSize: 10, fontFamily: 'inherit', cursor: 'pointer' }}>
            + note
          </button>
        ) : null}
      </div>
    );
  }

  // ── Re-match ────────────────────────────────────────────────────────
  // Auto-matching runs once at statement save; receipts recorded LATER
  // (Cimon's flow: see a receipt-less charge → jump to Receipts →
  // record it → come back) need this to pick them up. Deterministic,
  // same matcher, only touches still-unmatched lines; receipts already
  // matched on this statement are excluded.
  const [rematching, setRematching] = useState(false);
  async function rematchReceipts() {
    if (rematching || !piles.needsSort.length) return;
    setRematching(true);
    try {
      const taken = {};
      (lines || []).forEach(function(l) { if (l.matchedReceiptId) taken[l.matchedReceiptId] = true; });
      const available = receipts.filter(function(r) { return r && r.id && !taken[r.id]; });
      const results = matchCardStatementLines(piles.needsSort, {
        receipts: available, vendors: vendors, vendorPayments: vendorPayments,
      });
      let hits = 0;
      for (let i = 0; i < piles.needsSort.length; i++) {
        const m = results[i];
        if (!m) continue;
        if (m.status === 'matched') {
          const rcpt = receipts.find(function(r) { return r.id === m.receiptId; });
          await patchLine(piles.needsSort[i].id, {
            matched_receipt_id: m.receiptId,
            po_id: (rcpt && rcpt.poId) || null,
            match_status: 'matched',
            match_method: m.method,
          });
          hits++;
        } else if (m.status === 'payment' && m.poId) {
          await patchLine(piles.needsSort[i].id, {
            po_id: m.poId,
            match_method: m.method,
          });
          hits++;
        }
      }
      if (!hits) setSuggestError('No new matches — the amounts/dates don’t line up with any unmatched receipt or recorded vendor payment yet.');
      else setSuggestError('');
    } finally {
      setRematching(false);
    }
  }

  // ── AI smart sorting ────────────────────────────────────────────────
  // One cheap classification call per click: unsorted descriptors +
  // vendor directory + open POs + how similar descriptors were
  // classified on PAST statements (the precedent list — recurring
  // merchants converge on the human's prior answer). The endpoint
  // (api/suggest-statement-sort.js) sanitizes; here we only render
  // chips and apply on explicit Accept.
  async function suggestSorting() {
    if (suggesting || !piles.needsSort.length) return;
    setSuggesting(true);
    setSuggestError('');
    try {
      // Precedent: every previously classified line across ALL
      // statements (filtered client-side — 3 slim columns, cheap).
      const past = await supabase.from('card_statement_lines')
        .select('descriptor, category, po_id')
        .limit(1000);
      const pastClassifications = (past.data || [])
        .filter(function(r) { return r && (r.category || r.po_id); })
        .map(function(r) {
          if (r.category) return { descriptor: r.descriptor, choice: r.category };
          const po = posById[r.po_id];
          return { descriptor: r.descriptor, choice: 'po — vendor ' + ((po && (po.vendorName || po.vendor_name)) || 'unknown') };
        });

      const resp = await fetch('/api/suggest-statement-sort', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          lines: piles.needsSort.map(function(l) {
            return { id: l.id, date: l.lineDate, descriptor: l.descriptor, amount: l.amount };
          }),
          vendors: vendors.map(function(v) { return { name: v.name, aliases: v.aliases || [] }; }),
          // Per-PO money + history context is what disambiguates
          // SAME-VENDOR POs (Cimon 2026-07-12: "we use vendors over and
          // over so it must be beyond the vendor... it should use $
          // amount, deposit info, receipt info etc" + "also date — the
          // date doesn't have to be exact but it will help"). Status
          // rides along but is explicitly untrusted (Rule 10: money
          // records over status strings).
          purchaseOrders: purchaseOrders.map(function(p) {
            const poItems = (p.items || []).map(function(iid) {
              return items.find(function(it) { return it && it.id === iid; });
            }).filter(Boolean);
            const total = Math.round(poSubtotalLive(p, items) * 100) / 100;
            const expectedDeposit = Math.round(poItems.reduce(function(s, it) {
              return s + calcVendorDeposit(it.netPrice, it.qty, it.depositPct);
            }, 0) * 100) / 100;
            const orderDates = poItems.map(function(it) { return it.orderPlacedDate; })
              .filter(Boolean).sort();
            const pays = vendorPayments.filter(function(vp) {
              return vp && vp.poId === p.id && vp.paymentType !== 'Fee';
            });
            const paid = Math.round(pays.reduce(function(s, vp) {
              return s + (parseFloat(vp.amount) || 0);
            }, 0) * 100) / 100;
            const poReceipts = receipts.filter(function(r) {
              return r && r.poId === p.id && r.status !== 'rejected';
            }).slice(0, 5).map(function(r) {
              return { kind: r.kind, date: r.receiptDate, amount: parseFloat(r.totalAmount) || 0 };
            });
            return {
              id: p.id, ref: p.po_ref || '',
              vendor: p.vendorName || p.vendor_name || '',
              project: p.project || '', status: p.status || '',
              total: total, expectedDeposit: expectedDeposit,
              paidToVendor: paid,
              openBalance: Math.round((total - paid) * 100) / 100,
              orderPlaced: orderDates[0] || '',
              payments: pays.slice(0, 5).map(function(vp) {
                return { type: vp.paymentType, date: vp.paymentDate, amount: vp.amount };
              }),
              receipts: poReceipts,
            };
          }),
          pastClassifications: pastClassifications,
        }),
      });
      if (!resp.ok) {
        const j = await resp.json().catch(function() { return {}; });
        throw new Error(j.error || ('HTTP ' + resp.status));
      }
      const j = await resp.json();
      const byId = {};
      (j.suggestions || []).forEach(function(s) {
        if (s && s.id && s.kind !== 'unknown') byId[s.id] = s;
      });
      setSuggestions(byId);
      if (!Object.keys(byId).length) {
        setSuggestError('No confident suggestions this time — these need the human touch.');
      }
    } catch (err) {
      setSuggestError('Suggestions unavailable: ' + ((err && err.message) || err));
    } finally {
      setSuggesting(false);
    }
  }

  function acceptSuggestion(line) {
    const s = suggestions[line.id];
    if (!s) return;
    if (s.kind === 'po' && s.poId) patchLine(line.id, { po_id: s.poId });
    else if (s.kind === 'bucket' && s.bucket) patchLine(line.id, { category: s.bucket });
    setSuggestions(function(prev) {
      const next = Object.assign({}, prev);
      delete next[line.id];
      return next;
    });
  }

  function suggestionLabel(s) {
    if (s.kind === 'po') {
      const po = posById[s.poId];
      return 'PO ' + ((po && po.po_ref) || '') + ((po && po.project) ? ' (' + po.project + ')' : '');
    }
    return cardLineCategoryLabel(s.bucket);
  }

  function copyForDan() {
    const open = statements.find(function(s) { return s.id === openId; });
    const text = buildStatementSummaryText(open, lines || [], {
      receipts: receipts, purchaseOrders: purchaseOrders,
    });
    const done = function() { setCopied(true); setTimeout(function() { setCopied(false); }, 2500); };
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text).then(done, function() { window.prompt('Copy this:', text); });
    } else {
      window.prompt('Copy this:', text);
    }
  }

  // PO picker with suggested group first. Shared by needs-sort rows.
  function PoSelect(props) {
    const suggested = suggestPosForDescriptor(props.line.descriptor, purchaseOrders, vendors);
    const suggestedIds = {};
    suggested.forEach(function(p) { suggestedIds[p.id] = true; });
    return (
      <select
        value={props.line.poId || ''}
        data-testid={'assign-po-' + props.line.id}
        onChange={function(e) { patchLine(props.line.id, { po_id: e.target.value || null }); }}
        style={Object.assign({}, selectStyle, { maxWidth: 210 })}>
        <option value="">Assign PO…</option>
        {suggested.length ? (
          <optgroup label="Suggested — vendor named in the charge">
            {suggested.map(function(p) {
              return <option key={'s-' + p.id} value={p.id}>{poOptionLabel(p)}</option>;
            })}
          </optgroup>
        ) : null}
        <optgroup label={suggested.length ? 'All POs' : 'POs'}>
          {posSorted.filter(function(p) { return !suggestedIds[p.id]; }).map(function(p) {
            return <option key={p.id} value={p.id}>{poOptionLabel(p)}</option>;
          })}
        </optgroup>
      </select>
    );
  }

  // ── Render ──────────────────────────────────────────────────────────

  if (loadError) {
    return (
      <div style={{ padding: '18px 0', color: COLOR_ALERT, fontSize: 12, lineHeight: 1.6 }}>
        {loadError}
      </div>
    );
  }
  if (statements === null) {
    return <div style={{ padding: '18px 0', color: COLOR_MUTED, fontSize: 12 }}>Loading statements…</div>;
  }

  const open = openId ? statements.find(function(s) { return s.id === openId; }) : null;

  // ── Statement detail ────────────────────────────────────────────────
  if (open) {
    return (
      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 4, flexWrap: 'wrap' }}>
          <button onClick={function() { setOpenId(null); }} style={ghostBtn()}>← All statements</button>
          <div style={{ fontSize: 13, fontWeight: 500, color: COLOR_TEXT }}>
            {(open.label ? open.label + ' · ' : '') + (open.period || 'Statement')}
            <span style={{ color: COLOR_MUTED, fontWeight: 400, marginLeft: 8, fontSize: 11 }}>
              {open.filename} · {open.lineCount} lines
            </span>
            {(function() {
              const st = statementReconciliationState(lines || [], clientPayments);
              if (!st.total) return null;
              return st.closed
                ? <span data-testid="statement-reconciled" style={{ marginLeft: 10, fontSize: 11, fontWeight: 500, color: '#1A5C38' }}>✓ Reconciled</span>
                : <span style={{ marginLeft: 10, fontSize: 11, color: '#D4007A' }}>{st.unresolved} line{st.unresolved === 1 ? '' : 's'} to resolve</span>;
            })()}
          </div>
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
            <button
              onClick={downloadStatementForDan}
              data-testid="download-statement-csv"
              style={ghostBtn({ color: COLOR_ALERT, border: '0.5px solid ' + COLOR_ALERT })}>
              ↓ Spreadsheet for Dan
            </button>
            <button onClick={copyForDan} style={ghostBtn({ color: COLOR_ALERT, border: '0.5px solid ' + COLOR_ALERT })}>
              {copied ? '✓ Copied — paste into an email' : 'Copy list for Dan'}
            </button>
            {canManage ? (
              <button
                onClick={function() { confirmDelete ? deleteStatement() : setConfirmDelete(true); }}
                style={ghostBtn({ color: confirmDelete ? COLOR_ALERT : COLOR_MUTED,
                  border: '0.5px solid ' + (confirmDelete ? COLOR_ALERT : COLOR_BORDER_DK) })}>
                {confirmDelete ? 'Click again to delete this statement' : 'Delete statement'}
              </button>
            ) : null}
          </div>
        </div>

        {lines === null ? (
          <div style={{ padding: '18px 0', color: COLOR_MUTED, fontSize: 12 }}>Loading lines…</div>
        ) : (
          <div>
            {/* Pile 1 — needs sorting (first: it's the to-do list) */}
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 12 }}>
              {sectionTitle('Charges that still need sorting', piles.needsSort.length,
                piles.needsSort.length ? COLOR_ALERT : COLOR_TEXT)}
              {canManage && piles.needsSort.length ? (
                <React.Fragment>
                  <button
                    onClick={rematchReceipts}
                    disabled={rematching}
                    data-testid="rematch-receipts"
                    title="Check unsorted charges against receipts recorded since the statement was uploaded"
                    style={ghostBtn()}>
                    {rematching ? 'Checking…' : '↻ Re-match receipts'}
                  </button>
                  <button
                    onClick={suggestSorting}
                    disabled={suggesting}
                    data-testid="suggest-sorting"
                    style={ghostBtn({ color: COLOR_ALERT, border: '0.5px solid ' + COLOR_ALERT })}>
                    {suggesting ? 'Thinking…' : '✨ Suggest sorting'}
                  </button>
                </React.Fragment>
              ) : null}
            </div>
            <div style={{ fontSize: 11, color: COLOR_MUTED, marginBottom: 8 }}>
              Say what each one is: link its receipt, assign the PO (project spend), or pick a
              bucket — office, reimbursable, personal, card fees. Nothing here ever changes a
              receipt or a PO; you&rsquo;re only labeling the charge.
              {Object.keys(suggestions).length ? ' Suggestions apply only when you click Accept.' : ''}
            </div>
            {suggestError ? (
              <div style={{ fontSize: 11, color: COLOR_ALERT, marginBottom: 8 }}>{suggestError}</div>
            ) : null}
            {piles.needsSort.length === 0 ? (
              <div style={{ fontSize: 11, color: COLOR_PAID }}>✓ Every charge is sorted.</div>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead><tr>
                  <th style={head}>Date</th><th style={head}>Charge</th>
                  <th style={headRight}>Amount</th><th style={head}>What is it?</th>
                </tr></thead>
                <tbody>
                  {piles.needsSort.map(function(l) {
                    return (
                      <tr key={l.id}>
                        <td style={Object.assign({}, cell, { whiteSpace: 'nowrap' })}>{l.lineDate}</td>
                        <td style={cell}>{chargeDescCell(l)}</td>
                        <td style={Object.assign({}, cellRight, { color: COLOR_ALERT, fontWeight: 500 })}>
                          {fmtMoney(l.amount)}
                        </td>
                        <td style={cell}>
                          {canManage && suggestions[l.id] ? (
                            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap',
                              marginBottom: 6, fontSize: 11 }}>
                              <span style={{ color: COLOR_ALERT }}>
                                ✨ {suggestionLabel(suggestions[l.id])}
                                <span style={{ color: COLOR_MUTED, marginLeft: 6, fontSize: 10 }}>
                                  {suggestions[l.id].reason}
                                </span>
                              </span>
                              <button
                                onClick={function() { acceptSuggestion(l); }}
                                data-testid={'accept-suggestion-' + l.id}
                                style={ghostBtn({ color: COLOR_PAID, border: '0.5px solid ' + COLOR_PAID })}>
                                Accept
                              </button>
                            </div>
                          ) : null}
                          {canManage ? (
                            <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                              <PoSelect line={l} />
                              <select
                                value={l.category || ''}
                                data-testid={'category-' + l.id}
                                onChange={function(e) { patchLine(l.id, { category: e.target.value }); }}
                                style={Object.assign({}, selectStyle, { maxWidth: 190 })}>
                                <option value="">Or pick a bucket…</option>
                                {CARD_LINE_CATEGORIES.map(function(c) {
                                  return <option key={c.key} value={c.key}>{c.label}</option>;
                                })}
                              </select>
                              <select
                                defaultValue=""
                                data-testid={'link-receipt-' + l.id}
                                onChange={function(e) { if (e.target.value) linkReceipt(l, e.target.value); }}
                                style={Object.assign({}, selectStyle, { maxWidth: 240 })}>
                                <option value="">Link receipt…</option>
                                {linkOptions.map(function(r) {
                                  return <option key={r.id} value={r.id}>{receiptLabel(r)}</option>;
                                })}
                              </select>
                              <button onClick={function() { jumpTo('receipts'); }} style={ghostBtn()}>
                                Record in Receipts →
                              </button>
                              <button
                                onClick={function() { patchLine(l.id, { match_status: 'dismissed' }); }}
                                style={ghostBtn({ color: COLOR_MUTED })}
                                title="Not worth tracking — hide it">
                                Dismiss
                              </button>
                            </div>
                          ) : <span style={{ color: COLOR_MUTED, fontSize: 10 }}>—</span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}

            {/* Pile 2 — classified by hand */}
            {sectionTitle('Classified', piles.classified.length, COLOR_PAID)}
            {piles.classified.length === 0 ? (
              <div style={{ fontSize: 11, color: COLOR_MUTED, fontStyle: 'italic' }}>None yet.</div>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead><tr>
                  <th style={head}>Date</th><th style={head}>Charge</th>
                  <th style={headRight}>Amount</th><th style={head}>Classification</th><th style={head}></th>
                </tr></thead>
                <tbody>
                  {piles.classified.map(function(l) {
                    const po = l.poId ? posById[l.poId] : null;
                    return (
                      <tr key={l.id}>
                        <td style={Object.assign({}, cell, { whiteSpace: 'nowrap' })}>{l.lineDate}</td>
                        <td style={cell}>{chargeDescCell(l)}</td>
                        <td style={cellRight}>{fmtMoney(l.amount)}</td>
                        <td style={Object.assign({}, cell, { color: COLOR_PAID })}>
                          {po
                            ? 'Project — PO ' + (po.po_ref || '—') + (po.project ? ' (' + po.project + ')' : '')
                            : cardLineCategoryLabel(l.category)}
                          {po && !l.matchedReceiptId ? (
                            <span style={{ color: COLOR_MUTED, marginLeft: 6, fontSize: 10 }}>
                              no receipt recorded yet
                            </span>
                          ) : null}
                          {!po && l.category === 'reimbursable' && !l.matchedReceiptId ? (
                            <div style={{ color: '#B45309', fontSize: 10, marginTop: 2 }}>
                              needs a receipt — reimbursables bill to the client through the
                              receipt reader, always
                              <button onClick={function() { jumpTo('receipts'); }}
                                style={ghostBtn({ marginLeft: 8, height: 20, color: '#B45309', border: '0.5px solid #B45309' })}>
                                Record in Receipts →
                              </button>
                            </div>
                          ) : null}
                        </td>
                        <td style={Object.assign({}, cell, { textAlign: 'right' })}>
                          {canManage ? (
                            <button
                              onClick={function() { patchLine(l.id, { po_id: null, category: '' }); }}
                              style={ghostBtn({ color: COLOR_MUTED })}>
                              Clear
                            </button>
                          ) : null}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}

            {/* Pile 3 — matched to receipts */}
            {sectionTitle('Matched to receipts automatically', piles.matched.length, COLOR_PAID)}
            {piles.matched.length === 0 ? (
              <div style={{ fontSize: 11, color: COLOR_MUTED, fontStyle: 'italic' }}>
                None — receipts match when the amounts agree to the cent within a week.
              </div>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead><tr>
                  <th style={head}>Date</th><th style={head}>Charge</th>
                  <th style={headRight}>Amount</th><th style={head}>Receipt</th>
                  <th style={head}>PO</th><th style={head}></th>
                </tr></thead>
                <tbody>
                  {piles.matched.map(function(l) {
                    const r = l.matchedReceiptId ? receiptsById[l.matchedReceiptId] : null;
                    const po = l.poId ? posById[l.poId] : null;
                    return (
                      <tr key={l.id}>
                        <td style={Object.assign({}, cell, { whiteSpace: 'nowrap' })}>{l.lineDate}</td>
                        <td style={cell}>{chargeDescCell(l)}</td>
                        <td style={cellRight}>{fmtMoney(l.amount)}</td>
                        <td style={cell}>
                          {r ? (r.vendorName || '—') + ' · ' + (r.receiptDate || '')
                             : <span style={{ color: COLOR_ALERT }}>receipt deleted</span>}
                          <span style={{ color: COLOR_MUTED, marginLeft: 6, fontSize: 10 }}>
                            {l.matchMethod === 'manual' ? 'linked by hand' : 'auto-matched'}
                          </span>
                        </td>
                        <td style={cell}>{po ? (po.po_ref || '—') : '—'}</td>
                        <td style={Object.assign({}, cell, { textAlign: 'right' })}>
                          {canManage ? (
                            <button onClick={function() { unlinkReceipt(l); }} style={ghostBtn()}>Unlink</button>
                          ) : null}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}

            {/* Pile 4 — receipts without a charge (derived live) */}
            {sectionTitle('Receipts in this window with no card charge', noCharge.length,
              noCharge.length ? COLOR_ALERT : COLOR_TEXT)}
            <div style={{ fontSize: 11, color: COLOR_MUTED, marginBottom: 8 }}>
              Probably paid another way (wire / ACH / check / a different card) — or the
              recorded amount differs from the charge. Informational, nothing to fix here.
            </div>
            {noCharge.length === 0 ? (
              <div style={{ fontSize: 11, color: COLOR_MUTED, fontStyle: 'italic' }}>None.</div>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead><tr>
                  <th style={head}>Receipt date</th><th style={head}>Vendor</th>
                  <th style={headRight}>Amount</th><th style={head}>Kind</th><th style={head}>PO</th>
                </tr></thead>
                <tbody>
                  {noCharge.map(function(r) {
                    const po = r.poId ? posById[r.poId] : null;
                    return (
                      <tr key={r.id}>
                        <td style={Object.assign({}, cell, { whiteSpace: 'nowrap' })}>{r.receiptDate}</td>
                        <td style={cell}>{r.vendorName || '—'}</td>
                        <td style={cellRight}>{fmtMoney(parseFloat(r.totalAmount) || 0)}</td>
                        <td style={cell}>{r.kind || ''}</td>
                        <td style={cell}>{po ? (po.po_ref || '—') : '—'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}

            {/* Dismissed — restorable, one per row */}
            {piles.dismissed.length ? (
              <div style={{ marginTop: 18 }}>
                {sectionTitle('Dismissed', piles.dismissed.length, COLOR_MUTED)}
                <div style={{ fontSize: 11, color: COLOR_MUTED, marginBottom: 8 }}>
                  Hidden from the sorting list. Restore puts a charge back so you can
                  classify it instead.
                </div>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <tbody>
                    {piles.dismissed.map(function(l) {
                      return (
                        <tr key={l.id}>
                          <td style={Object.assign({}, cell, { whiteSpace: 'nowrap', color: COLOR_MUTED })}>{l.lineDate}</td>
                          <td style={Object.assign({}, cell, { color: COLOR_MUTED })}>{l.descriptor}</td>
                          <td style={Object.assign({}, cellRight, { color: COLOR_MUTED })}>{fmtMoney(l.amount)}</td>
                          <td style={Object.assign({}, cell, { textAlign: 'right' })}>
                            {canManage ? (
                              <button
                                data-testid={'restore-' + l.id}
                                onClick={function() { patchLine(l.id, { match_status: 'unmatched' }); }}
                                style={ghostBtn()}>
                                Restore
                              </button>
                            ) : null}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : null}
            {incoming.length ? (
              <div style={{ marginTop: 6 }}>
                {sectionTitle('Incoming money on this statement',
                  incoming.length,
                  incoming.some(function(e) { return e.match.status === 'unrecorded'; }) ? COLOR_ALERT : COLOR_TEXT)}
                <div style={{ fontSize: 11, color: COLOR_MUTED, marginBottom: 8 }}>
                  Deposits are checked against payments recorded through the client-payment
                  wizard. A wire with no recorded payment usually means the wizard was never
                  run for it — record it and this row clears itself.
                </div>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead><tr>
                    <th style={head}>Date</th><th style={head}>Line</th>
                    <th style={headRight}>Amount</th><th style={head}>Status</th>
                  </tr></thead>
                  <tbody>
                    {incoming.map(function(e) {
                      const l = e.line, m = e.match;
                      const pay = m.status === 'recorded' ? paymentsById[m.paymentId] : null;
                      const who = m.status === 'unrecorded'
                        ? suggestDepositSource(l.descriptor, clients, projects) : '';
                      return (
                        <tr key={l.id}>
                          <td style={Object.assign({}, cell, { whiteSpace: 'nowrap' })}>{l.lineDate}</td>
                          <td style={cell}>{chargeDescCell(l)}</td>
                          <td style={cellRight}>{fmtMoney(Math.abs(l.amount))}</td>
                          <td style={cell}>
                            {m.status === 'recorded' ? (
                              <span style={{ color: COLOR_PAID }}>
                                ✓ Recorded{pay ? ' — ' + describeRecordedPayment(pay, paymentAllocations) : ''}
                                {(function() {
                                  const docs = pay ? describeRecordedPaymentDocs(pay, paymentAllocations, ctx.invoices || []) : '';
                                  return docs ? <span style={{ color: COLOR_MUTED }}>{' → ' + docs}</span> : null;
                                })()}
                              </span>
                            ) : m.status === 'card_payment' ? (
                              <span style={{ color: COLOR_MUTED }}>Card payment / refund — not client money</span>
                            ) : m.status === 'ambiguous' ? (
                              <span style={{ color: COLOR_MUTED }}>
                                Matches {m.paymentIds.length} recorded payments of the same amount — check dates by hand
                              </span>
                            ) : (
                              <span style={{ color: COLOR_ALERT }}>
                                ⚠ No recorded payment matches
                                {who ? ' — looks like ' + who : ''}
                                <button
                                  onClick={function() { jumpTo('invoices'); }}
                                  style={ghostBtn({ marginLeft: 8, color: COLOR_ALERT, border: '0.5px solid ' + COLOR_ALERT })}>
                                  Record it → Billing History
                                </button>
                              </span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : null}
          </div>
        )}
      </div>
    );
  }

  // ── Statement list + upload ───────────────────────────────────────────

  // Account pills derive from the labels actually on file — new
  // accounts appear automatically, none are hardcoded.
  const accounts = [];
  statements.forEach(function(s) {
    const a = (s.label || '').trim();
    if (a && accounts.indexOf(a) < 0) accounts.push(a);
  });
  accounts.sort();
  const visibleStatements = accountFilter === 'all'
    ? statements
    : statements.filter(function(s) { return (s.label || '').trim() === accountFilter; });

  function accountPill(active) {
    return {
      height: 28, padding: '0 12px', borderRadius: 14,
      border: active ? '1.5px solid ' + COLOR_TEXT : '0.5px solid ' + COLOR_BORDER_DK,
      background: COLOR_GROUND, color: active ? COLOR_TEXT : COLOR_MUTED,
      fontSize: 11, fontWeight: 500, fontFamily: 'inherit',
      cursor: 'pointer', whiteSpace: 'nowrap',
    };
  }

  return (
    <div>
      {canManage ? (
        <div style={{ marginBottom: 18 }}>
          <label style={{
            display: 'inline-flex', alignItems: 'center', gap: 8,
            height: 32, padding: '0 14px', borderRadius: 6,
            border: '1px solid ' + COLOR_ALERT, color: COLOR_ALERT,
            background: COLOR_GROUND, fontSize: 12, fontWeight: 500, cursor: 'pointer',
          }}>
            Upload statement CSV
            <input type="file" accept=".csv,text/csv" onChange={onPickFile} style={{ display: 'none' }} />
          </label>
          <span style={{ marginLeft: 10, fontSize: 11, color: COLOR_MUTED }}>
            Amex, the office card, or a bank account — download the month as CSV and drop it here.
          </span>
        </div>
      ) : null}

      {pending && pendingParsed ? (
        <div style={{ border: '0.5px solid ' + COLOR_BORDER_DK, borderRadius: 6,
          padding: '14px 16px', marginBottom: 18, maxWidth: 620 }}>
          <div style={{ fontSize: 12, fontWeight: 500, color: COLOR_TEXT, marginBottom: 8 }}>
            {pending.filename}
          </div>
          <div style={{ fontSize: 11, color: COLOR_TEXT, lineHeight: 1.7 }}>
            {pendingParsed.chargeCount} charges totaling {fmtMoney(pendingParsed.chargeTotal)}
            {pendingParsed.creditCount ? ' · ' + pendingParsed.creditCount + ' payments/credits' : ''}
            {pendingParsed.dateMin ? ' · ' + pendingParsed.dateMin + ' → ' + pendingParsed.dateMax : ''}
            {pendingParsed.lines.length === 0 ? (
              <div data-testid="statement-unreadable" style={{ color: COLOR_ALERT, fontWeight: 500 }}>
                Couldn&rsquo;t read any rows from this file — the column layout isn&rsquo;t one
                Gilda knows yet. Send the file to Cimon/Claude to teach the parser; nothing
                was saved.
              </div>
            ) : pendingParsed.errors.length ? (
              <div style={{ color: COLOR_ALERT }}>
                {pendingParsed.errors.length} line{pendingParsed.errors.length === 1 ? '' : 's'} skipped (unreadable date or amount).
              </div>
            ) : null}
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 10,
            fontSize: 11, color: COLOR_TEXT, cursor: 'pointer' }}>
            <input
              type="checkbox" checked={pending.flip}
              onChange={function(e) { setPending(Object.assign({}, pending, { flip: e.target.checked })); }}
            />
            This file records spending as negative (bank style) — flip the signs
            {detectSpendNegative(pending.parsedOriginal) ? (
              <span style={{ color: COLOR_MUTED }}>· detected automatically</span>
            ) : null}
          </label>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 12, flexWrap: 'wrap' }}>
            <label style={{ fontSize: 10, color: COLOR_MUTED, textTransform: 'uppercase', letterSpacing: '0.07em' }}>
              Account
              <select
                value={accountOptions.indexOf(pending.account) >= 0 ? pending.account : '__new__'}
                data-testid="statement-account-select"
                onChange={function(e) {
                  const v = e.target.value;
                  setPending(Object.assign({}, pending, {
                    account: v === '__new__' ? '' : v,
                  }));
                }}
                style={{ marginLeft: 8, height: 26, padding: '0 6px',
                  border: '0.5px solid ' + COLOR_BORDER_DK, borderRadius: 4,
                  fontSize: 11, fontFamily: 'inherit', background: '#fff' }}>
                {accountOptions.map(function(o) {
                  return <option key={o} value={o}>{o}</option>;
                })}
                <option value="__new__">New account…</option>
              </select>
            </label>
            {accountOptions.indexOf(pending.account) < 0 ? (
              <input
                type="text" value={pending.account}
                data-testid="statement-account"
                onChange={function(e) { setPending(Object.assign({}, pending, { account: e.target.value })); }}
                placeholder="Name the account (e.g. Chase checking)"
                autoCorrect="off" autoCapitalize="none" spellCheck={false} autoComplete="off"
                style={{ height: 26, width: 170, padding: '0 8px',
                  border: '0.5px solid ' + COLOR_BORDER_DK, borderRadius: 4,
                  fontSize: 11, fontFamily: 'inherit' }}
              />
            ) : null}
            <label style={{ fontSize: 10, color: COLOR_MUTED, textTransform: 'uppercase', letterSpacing: '0.07em' }}>
              Months
              <span data-testid="statement-months" style={{ marginLeft: 8, fontSize: 11, color: COLOR_TEXT, textTransform: 'none', letterSpacing: 0 }}>
                {(function() {
                  const groups = splitStatementByMonth(pendingParsed);
                  if (!groups.length) return '—';
                  return groups.map(function(g) { return g.period + ' (' + g.lines.length + ')'; }).join(' · ');
                })()}
              </span>
            </label>
            <button
              onClick={saveStatement}
              disabled={saving || !pendingParsed.lines.length}
              style={{
                height: 28, padding: '0 16px', borderRadius: 6, border: 'none',
                background: COLOR_TEXT, color: COLOR_GROUND, fontSize: 12,
                fontWeight: 500, fontFamily: 'inherit',
                cursor: saving ? 'wait' : 'pointer',
                opacity: pendingParsed.lines.length ? 1 : 0.5,
              }}>
              {saving ? 'Matching…' : 'Save & match'}
            </button>
            <button onClick={function() { setPending(null); }} style={ghostBtn()}>Cancel</button>
          </div>
        </div>
      ) : null}

      {accounts.length > 1 ? (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 14 }}>
          <button onClick={function() { setAccountFilter('all'); }} style={accountPill(accountFilter === 'all')}>
            All accounts
          </button>
          {accounts.map(function(a) {
            return (
              <button key={a} onClick={function() { setAccountFilter(a); }} style={accountPill(accountFilter === a)}>
                {a}
              </button>
            );
          })}
        </div>
      ) : null}

      {(function() {
        const periods = [];
        statements.forEach(function(s) {
          if (s.period && periods.indexOf(s.period) < 0) periods.push(s.period);
        });
        periods.sort().reverse();
        if (!periods.length) return null;
        const chosen = periods.indexOf(exportPeriod) >= 0 ? exportPeriod : periods[0];
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16,
            fontSize: 11, color: COLOR_MUTED }}>
            Monthly spreadsheet for Dan — every account in one file:
            <select
              value={chosen}
              data-testid="export-period"
              onChange={function(e) { setExportPeriod(e.target.value); }}
              style={Object.assign({}, selectStyle, { height: 26 })}>
              {periods.map(function(p) { return <option key={p} value={p}>{p}</option>; })}
            </select>
            <button
              onClick={function() { downloadMonthForDan(chosen); }}
              disabled={exporting}
              data-testid="download-month-csv"
              style={ghostBtn({ color: COLOR_ALERT, border: '0.5px solid ' + COLOR_ALERT, height: 26 })}>
              {exporting ? 'Building…' : '↓ Download month (.csv)'}
            </button>
          </div>
        );
      })()}

      {statements.length === 0 && !pending ? (
        <div style={{ padding: '40px 0', textAlign: 'center', color: COLOR_MUTED,
          fontSize: 12, fontStyle: 'italic',
          border: '0.5px dashed ' + COLOR_BORDER, borderRadius: 6 }}>
          No statements uploaded yet. Upload a month&rsquo;s CSV to cross-check
          card or bank charges against recorded receipts.
        </div>
      ) : statements.length ? (
        <table style={{ width: '100%', maxWidth: 760, borderCollapse: 'collapse' }}>
          <thead><tr>
            <th style={head}>Account</th><th style={head}>Period</th><th style={head}>File</th>
            <th style={headRight}>Lines</th><th style={head}>Status</th><th style={head}>Uploaded</th><th style={head}></th>
          </tr></thead>
          <tbody>
            {visibleStatements.map(function(s) {
              const st = listLines ? statementReconciliationState(listLines[s.id] || [], clientPayments) : null;
              return (
                <tr key={s.id}>
                  <td style={Object.assign({}, cell, { fontWeight: 500 })}>
                    {canManage ? (
                      <select
                        value={s.label || ''}
                        data-testid={'statement-account-edit-' + s.id}
                        onChange={function(e) {
                          let v = e.target.value;
                          if (v === '__new__') {
                            v = window.prompt('Name the account (e.g. Chase bank account):') || '';
                            v = v.trim();
                            if (!v) return;
                          }
                          updateStatementAccount(s.id, v);
                        }}
                        style={{ border: 'none', background: 'transparent', fontSize: 12,
                          fontWeight: 500, fontFamily: 'inherit', color: s.label ? '#111111' : '#D4007A',
                          cursor: 'pointer', maxWidth: 170 }}>
                        {!s.label ? <option value="">— set account</option> : null}
                        {accountOptions.map(function(o) {
                          return <option key={o} value={o}>{o}</option>;
                        })}
                        {s.label && accountOptions.indexOf(s.label) < 0
                          ? <option value={s.label}>{s.label}</option> : null}
                        <option value="__new__">New account…</option>
                      </select>
                    ) : (s.label || '—')}
                  </td>
                  <td style={cell}>{s.period || '—'}</td>
                  <td style={cell}>{s.filename || '—'}</td>
                  <td style={cellRight}>{s.lineCount}</td>
                  <td style={cell} data-testid={'statement-status-' + s.id}>
                    {!st || !st.total ? <span style={{ color: COLOR_MUTED }}>—</span>
                      : st.closed ? <span style={{ color: '#1A5C38', fontWeight: 500 }}>✓ Reconciled</span>
                      : <span style={{ color: '#D4007A' }}>{st.unresolved} to resolve</span>}
                  </td>
                  <td style={Object.assign({}, cell, { color: COLOR_MUTED })}>
                    {String(s.createdAt).slice(0, 10)}
                  </td>
                  <td style={Object.assign({}, cell, { textAlign: 'right' })}>
                    <button onClick={function() { setOpenId(s.id); }} style={ghostBtn()}>Open</button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      ) : null}
    </div>
  );
}
